<template lang="html">
  <prt-modal class="prt-search" :show="show" @close="cancel">
    <prt-autocomplete-bubble :key="bubble.id" v-for="bubble in suggestions" @click.native="sendSearch($event, bubble.query, bubble.content, bubble.range)">{{bubble.content}}</prt-autocomplete-bubble>
    <div class="search-field-wrapper">
      <input :placeholder="placeholder" ref="search-input" type="search" v-model="query" @keyup.enter="sendSearch">
      <button aria-label="Search" class="submit-button" @click="sendSearch">
        <prt-icon icon="send" />
      </button>
    </div>
  </prt-modal>
</template>

<script>
import { endOfWeek, format, startOfMonth, startOfWeek, startOfYear, subMonths, subYears } from 'date-fns';
import { Date as SugarDate } from 'sugar-date';

import PrtAutocompleteBubble from './PrtAutocompleteBubble.vue';
import PrtIcon from './PrtIcon.vue';
import PrtModal from './PrtModal.vue';

export default {
  components: {
    PrtAutocompleteBubble,
    PrtIcon,
    PrtModal,
  },
  data() {
    return {
      loaderTimeout: null,
      query: '',
      suggestions: [
        {
          content: 'Last Month',
          id: 1,
          query: subMonths(startOfMonth(Date.now()), 1),
          range: 'month',
        },
        {
          content: `Last ${format(subMonths(startOfMonth(Date.now()), 2), 'MMMM')}`,
          id: 2,
          query: subMonths(startOfMonth(Date.now()), 2),
          range: 'month',
        },
        {
          content: 'This Year',
          id: 3,
          query: startOfYear(Date.now()),
          range: 'year',
        },
        {
          content: format(subYears(startOfYear(Date.now()), 1), 'YYYY'),
          id: 4,
          query: subYears(startOfYear(Date.now()), 1),
          range: 'year',
        },
      ],
    };
  },
  methods: {
    cancel() {
      this.$emit('close');
    },
    sendSearch(event, search, humanReadableQuery, range) {
      let query;
      let filter = null;
      let type;
      if (!search) {
        query = this.query.toLowerCase().trim();

        if (!query) {
          this.$store.dispatch('cancelSearch').then(this.cancel);
          return;
        }

        if (query.indexOf('~') > -1) {
          const split = query.split('~');
          [, query] = split;
          filter = split[0].toLowerCase().trim();
        }

        const parsedDate = SugarDate.create(query, { locale: 'en-GB', past: true });

        if ((parsedDate instanceof Date && Number.isNaN(parsedDate.valueOf())) || SugarDate.isFuture(parsedDate)) {
          type = 'description';
          query = this.query.toLowerCase().trim();
        } else {
          if (query.includes('week')) range = 'week'; // eslint-disable-line no-param-reassign
          else if (['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'month']
            .some(el => query.includes(el)) && (!query.match(/\d+/) || query.match(/\d+/)[0] >= 31)) range = 'month'; // eslint-disable-line no-param-reassign
          else if (query.includes('year') || parseInt(query, 10) >= 1000) range = 'year'; // eslint-disable-line no-param-reassign
          else range = 'day'; // eslint-disable-line no-param-reassign
          humanReadableQuery = query; // eslint-disable-line no-param-reassign

          switch (range) {
            case 'week':
              if (filter !== null) humanReadableQuery = `${filter} during ${format(startOfWeek(parsedDate, { weekStartsOn: 1 }), 'DD MMM')} to ${format(endOfWeek(parsedDate, { weekStartsOn: 1 }), 'DD MMM YYYY')}`; // eslint-disable-line no-param-reassign
              else humanReadableQuery = `${format(startOfWeek(parsedDate, { weekStartsOn: 1 }), 'ddd, DD MMM')} to ${format(endOfWeek(parsedDate, { weekStartsOn: 1 }), 'ddd, DD MMM YYYY')}`; // eslint-disable-line no-param-reassign
              break;
            case 'month':
              if (filter !== null) humanReadableQuery = `${filter} in ${format(parsedDate, 'MMM YYYY')}`; // eslint-disable-line no-param-reassign
              else humanReadableQuery = format(parsedDate, 'MMMM YYYY'); // eslint-disable-line no-param-reassign
              break;
            case 'year':
              if (filter !== null) humanReadableQuery = `${filter} in ${format(parsedDate, 'YYYY')}`; // eslint-disable-line no-param-reassign
              else humanReadableQuery = `Year ${format(parsedDate, 'YYYY')}`; // eslint-disable-line no-param-reassign
              break;
            default:
              if (filter !== null) humanReadableQuery = `${filter} on ${format(parsedDate, 'DD MMM YYYY')}`; // eslint-disable-line no-param-reassign
              else humanReadableQuery = format(parsedDate, 'DD MMMM YYYY'); // eslint-disable-line no-param-reassign
          }

          query = parsedDate;
          type = 'date';
        }
      } else {
        query = search;
        type = 'date';
      }

      // set a timeout to show a loader in case the search takes a little longer
      this.loaderTimeout = window.setTimeout(() => {
        this.$store.commit('setLoadingModal', 'Assembling search results…');
        this.loaderTimeout = null;
      }, 200);

      this.$store.commit('updateQuery', { query: humanReadableQuery || query });
      this.$store.dispatch('search', { query, range, type, filter }) // eslint-disable-line object-curly-newline
        .then(() => {
          // clear the loaderTimeout before it gets shown
          if (this.loaderTimeout) {
            window.clearTimeout(this.loaderTimeout);
            this.loaderTimeout = null;
          }
          // remove the loader if it's shown
          if (this.$store.state.application.loadingModal) this.$store.commit('setLoadingModal', '');
        })
        .catch(err => this.$store.commit('addToast', { color: 'negative', content: `Error while searching: ${err.message}`, timeout: 5000 }));
      this.$emit('close');
    },
  },
  props: {
    placeholder: {
      default: 'Search Term',
      type: String,
    },
    show: Boolean,
  },
  watch: {
    show(newVal) {
      if (newVal) this.$nextTick(() => this.$refs['search-input'].focus());
    },
  },
};
</script>

<style lang="stylus" scoped>
   @require '../../styles/colors'

   .prt-search
     display: flex
     justify-content: flex-end
     align-items: center
     flex-direction: column

     .search-field-wrapper
       display: flex
       height: 56px
       width: calc(100% - 32px)
       max-width: 504px
       box-shadow: 0 2px 4px black-12
       border-radius: (@height / 2)px
       margin: 16px auto
       background-color: white

       input
         display: block
         height: 56px
         width: calc(100% - 68px)
         border: none
         outline: none
         font-family: inherit
         font-size: inherit
         background-color: transparent
         margin-left: 20px
         text-overflow: ellipsis
         color: inherit

         &::-webkit-search-decoration,
         &::-webkit-search-cancel-button,
         &::-webkit-search-results-button,
         &::-webkit-search-results-decoration
          -webkit-appearance: none

      .submit-button
        background-color: positive
        box-shadow: 0 2px 4px black-12
        color: white
        border: none
        outline: none
        width: 48px
        height: @width
        margin: 4px
        border-radius: (@width / 2)px
        box-sizing: border-box
        padding: 15px
        cursor: pointer
        -webkit-tap-highlight-color: transparent
        transition: background-color 200ms ease

        &:active
          background-color: lighten(positive, 30%)
          transform: translateY(2px)
</style>
